import moment from 'moment'
import Modal from '@/components/layout/Modal'
import type { DrinkEntry } from '@/types'
import { getBACStatus } from '@/utils/bac'
import { standardDrinks } from '@/utils/health'
import {
  formatNumber,
  mlToDisplay,
  toCompactDuration,
  toHHMMSS,
  volumeUnit,
} from '@/utils/time'

/**
 * Everything the modal needs about one history row. Built by DrinkHistory,
 * which already has the session indices and duration map in hand.
 */
export interface EntryDetail {
  entry: DrinkEntry
  originalIndex: number
  sessionNumber: number | null // 1-based, null for gaps
  totalSessions: number
  positionInSession: number | null // 1-based position within its session
  sessionDrinkCount: number | null
  sessionPeakBac: number | null
  sessionStart: string | null
  sessionEnd: string | null
  secondsSincePrevious: number | null
}

interface DrinkDetailModalProps {
  detail: EntryDetail | null
  onClose: () => void
  onEdit: (index: number) => void
  onCopy: (index: number) => void
  onDelete: (index: number) => void
}

/** Full precise timestamp — unlike formatHistoryDate this keeps the time on mobile. */
function fullTimestamp(dateStr: string): string {
  return moment(dateStr).format('ddd D MMM YYYY, HH:mm')
}

function safeParse(value: string): number | null {
  const n = parseFloat(value)
  return Number.isFinite(n) ? n : null
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="bg-[var(--bg-card)] rounded-lg px-3 py-2">
      <div className="text-[0.7rem] uppercase tracking-wide text-[var(--text-secondary)]">{label}</div>
      <div className={`mt-0.5 font-medium ${tone || 'text-[var(--text-primary)]'}`}>{value}</div>
    </div>
  )
}

/**
 * Text colour for a BAC value. Mirrors getBACColorClass()'s thresholds, but
 * those classes set a *background*; the critical shade (#18181b) is unreadable
 * as text on the dark card, so critical reuses the danger red.
 */
function bacTextColor(bac: number): string {
  if (bac >= 0.1) return 'var(--bac-danger)'
  if (bac >= 0.05) return 'var(--bac-caution)'
  if (bac >= 0.01) return 'var(--text-primary)'
  return 'var(--bac-safe)'
}

/** Prominent session-total badge shown across the top of the modal. */
function Badge({ value, label, color, mono }: { value: string; label: string; color?: string; mono?: boolean }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg px-2 py-3 text-center">
      <div
        className={`text-xl font-semibold leading-tight ${mono ? 'font-mono' : ''}`}
        style={{ color: color || 'var(--text-primary)' }}
      >
        {value}
      </div>
      <div className="mt-1 text-[0.7rem] uppercase tracking-wide text-[var(--text-secondary)]">
        {label}
      </div>
    </div>
  )
}

export default function DrinkDetailModal({
  detail,
  onClose,
  onEdit,
  onCopy,
  onDelete,
}: DrinkDetailModalProps) {
  if (!detail) return null

  const { entry, originalIndex } = detail
  const isGap = !!entry.is_gap

  const handleDelete = () => {
    const message = isGap
      ? 'Are you sure you want to delete this gap?'
      : 'Are you sure you want to delete this entry?'
    if (confirm(message)) {
      onDelete(originalIndex)
      onClose()
    }
  }

  // ---- Gap entries -------------------------------------------------------
  if (isGap) {
    const start = entry.started_at || entry.finished_at
    const gapSeconds = Math.max(
      0,
      (new Date(entry.finished_at).getTime() - new Date(start).getTime()) / 1000
    )

    return (
      <Modal isOpen onClose={onClose} title="Data Gap" size="lg">
        <div className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">
            A period with no tracking. Gaps are excluded from sobriety streaks and abstinence
            reporting so untracked time is never counted as sober.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Stat label="Started" value={fullTimestamp(start)} />
            <Stat label="Ended" value={fullTimestamp(entry.finished_at)} />
            <Stat label="Duration" value={toCompactDuration(gapSeconds)} />
            <Stat label="Exact" value={toHHMMSS(gapSeconds)} />
          </div>

          <div className="flex justify-end pt-2">
            <button onClick={handleDelete} className="btn-danger">
              Delete Gap
            </button>
          </div>
        </div>
      </Modal>
    )
  }

  // ---- Drink entries -----------------------------------------------------
  const ml = safeParse(entry.drink.milliliters)
  const abv = safeParse(entry.drink.alcohol)
  const calories = safeParse(entry.drink.calories)
  const std = standardDrinks(entry)
  const bac = entry.bac || 0

  const sessionSeconds =
    detail.sessionStart && detail.sessionEnd
      ? Math.max(
          0,
          (new Date(detail.sessionEnd).getTime() - new Date(detail.sessionStart).getTime()) / 1000
        )
      : null

  return (
    <Modal isOpen onClose={onClose} title={entry.drink.drink || 'Drink'} size="lg">
      <div className="space-y-4">
        {/* Session totals — the whole session this drink belongs to, not a
            running total up to this drink. */}
        <div>
          <div className="grid grid-cols-3 gap-2">
            <Badge
              value={detail.sessionDrinkCount != null ? String(detail.sessionDrinkCount) : '—'}
              label="Drinks"
            />
            <Badge
              value={sessionSeconds != null ? toCompactDuration(sessionSeconds) : '—'}
              label="Duration"
            />
            <Badge
              value={detail.sessionPeakBac != null ? detail.sessionPeakBac.toFixed(3) : '—'}
              label="BAC"
              mono
              color={detail.sessionPeakBac != null ? bacTextColor(detail.sessionPeakBac) : undefined}
            />
          </div>
          <p className="mt-1.5 text-xs text-[var(--text-secondary)] text-center">
            Totals for the whole session{detail.sessionNumber != null ? ` (session ${detail.sessionNumber} of ${detail.totalSessions})` : ''} — peak BAC, not the value at this drink
          </p>
        </div>

        {/* Timestamp */}
        <div>
          <div className="text-sm text-[var(--text-secondary)]">Logged</div>
          <div className="text-lg font-medium text-[var(--text-primary)]">
            {fullTimestamp(entry.finished_at)}
          </div>
        </div>

        {/* Drink facts */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Stat
            label={`Volume (${volumeUnit()})`}
            value={ml != null ? formatNumber(mlToDisplay(ml), 0) : '—'}
          />
          <Stat label="Alcohol" value={abv != null ? `${abv}%` : '—'} />
          <Stat label="Calories" value={calories != null ? `${formatNumber(calories, 0)} kcal` : '—'} />
          <Stat
            label="Standard drinks"
            value={Number.isFinite(std) ? formatNumber(std, 1) : '—'}
          />
          <Stat
            label="BAC after"
            value={`${bac.toFixed(3)}%`}
            tone="font-mono text-[var(--text-primary)]"
          />
          <Stat label="Level" value={getBACStatus(bac)} />
        </div>

        {/* Session context */}
        <div className="border border-[var(--border)] rounded-lg p-3 space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <h4 className="font-semibold text-[var(--text-primary)]">
              {detail.sessionNumber != null
                ? `Session ${detail.sessionNumber} of ${detail.totalSessions}`
                : 'Session'}
            </h4>
            {detail.positionInSession != null && detail.sessionDrinkCount != null && (
              <span className="text-sm text-[var(--text-secondary)]">
                Drink {detail.positionInSession} of {detail.sessionDrinkCount}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {detail.sessionStart && (
              <Stat label="Session started" value={fullTimestamp(detail.sessionStart)} />
            )}
            {detail.sessionEnd && (
              <Stat label="Last drink" value={fullTimestamp(detail.sessionEnd)} />
            )}
          </div>

          <div className="text-sm text-[var(--text-secondary)]">
            Time since previous drink:{' '}
            <span className="font-mono text-[var(--text-primary)]">
              {detail.secondsSincePrevious != null
                ? toHHMMSS(detail.secondsSincePrevious)
                : '— (first drink of the session)'}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button
            onClick={() => {
              onCopy(originalIndex)
              onClose()
            }}
            className="btn-secondary"
          >
            Copy
          </button>
          <button
            onClick={() => {
              onEdit(originalIndex)
              onClose()
            }}
            className="btn-primary"
          >
            Edit
          </button>
          <button onClick={handleDelete} className="btn-danger">
            Delete
          </button>
        </div>
      </div>
    </Modal>
  )
}
